import { GenerateNumberRequest } from 'src/Api/MpOrders';
import { useForm } from 'src/Hooks/useFormWrapper';
import { useCallback } from 'react';
import { useSelectedOrdersRows } from 'src/Hooks';
import { salesPageAsyncActions } from 'src/Pages/Sales/services';
import { modalWindowsActions } from 'src/ModalWindows/services';
import { ModalType, NumberGenerationType } from 'src/Enums';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/Redux/ConfigureStore';
import { generateDocumentNumberModalActions } from '../services/reducer';
import { DocumentTemplateRow } from '@/Types';
import { documentPrintModalActions } from '@/ModalWindows/DocumentPrintModal/services/reducer';
import { emailSendModalActions } from '@/ModalWindows/EmailSendModal/services/reducer';
import { EmailTemplateRow } from '@/Types/EmailTemplateRow';
import { unwrapResult } from '@reduxjs/toolkit';

export type FormData = GenerateNumberRequest;

export const useGenerateDocumentNumbersForm = (genType?: NumberGenerationType) => {
  const dispatch: AppDispatch = useDispatch();

  const methods = useForm<FormData>({
    shouldUnregister: false,
    mode: 'onChange',
    defaultValues: {
      isSortWhenPrinting: false,
    },
  });

  const { orderIds } = useSelectedOrdersRows();

  const handleSalesPage = useCallback(
    async (data: FormData, genType: NumberGenerationType) => {
      const result = await dispatch(salesPageAsyncActions.checkGeneratedNumberForOrder({ genType, orderIds }));
      const { warnings } = unwrapResult(result);

      if (warnings?.length) {
        dispatch(
          modalWindowsActions.openModal({
            modalType: ModalType.AlreadyExistsDocumentNumber,
            params: warnings[0].localizationParams,
          }),
        );
      } else {
        dispatch(salesPageAsyncActions.generateNumberForOrder({ ...data, genType, orderIds })).then((response: any) => {
          if (!response.error) {
            dispatch(modalWindowsActions.closeModal());
          }
        });
      }
    },
    [orderIds],
  );

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (orderIds.length <= 0 || genType == null) return;

      dispatch(generateDocumentNumberModalActions.setNumberGenerationRequest({ ...data, orderIds, genType }));

      return handleSalesPage(data, genType);
    },
    [genType, orderIds],
  );

  const onCountCopyChange = (row: DocumentTemplateRow) => (value: string) => {
    dispatch(documentPrintModalActions.setGenerateNumberCountCopy({ guid: row.guid, count: Number(value) }));
  };

  const onIsSortChange = (row: DocumentTemplateRow) => (value: boolean) => {
    dispatch(documentPrintModalActions.setGenerateNumberSortWhenPrinting({ guid: row.guid, isSort: value }));
  };

  const onPrintChange = (row: DocumentTemplateRow) => (value: boolean) => {
    dispatch(documentPrintModalActions.setGenerateNumberPrintDocument({ guid: row.guid, print: value }));
  };

  const onSendEmailChange = (row: EmailTemplateRow) => (value: boolean) => {
    dispatch(emailSendModalActions.setGenerateNumberSendEmail({ guid: row.guid, sendEmail: value }));
  };

  return {
    onSubmit: methods.handleSubmit(onSubmit),
    onCountCopyChange,
    onIsSortChange,
    onPrintChange,
    onSendEmailChange,
    methods,
  };
};
